import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCheck,
  faEyeSlash,
  faLayerGroup,
  faLock,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getItemMeta } from '../constants/checklistItemMeta';
import {
  buildOnboardingPath,
  buildStepUploads,
  uploadHubNote,
  findBlockingStep,
  getPathStats,
  sortItemsForPath,
} from '../constants/onboardingPaths';
import { useFetchDeclarant } from 'modules/Declarant/hooks/useFetchDeclarant';
import { isUkRegulation } from 'modules/Common/utils/regulation';
import { parseStepHowTo } from '../utils/parseStepHowTo';
import OnboardingStepVideo from './OnboardingStepVideo';
import OnboardingUploadOptions from './OnboardingUploadOptions';

/**
 * OnboardingGuidedPath — design `3b`.
 *
 * A dependency-aware journey over the one seeded checklist, drawn as a single
 * rail: every step is on screen at once, and exactly one of them is expanded
 * with its real CTA. There is one path and one framing — getting the report
 * filed (D18) — so the panel opens straight onto it, with nothing to choose
 * first and nothing to switch between.
 *
 * ── Expansion ──────────────────────────────────────────────────────────────
 * By default the open step is the first incomplete one, so the panel opens on
 * the thing to do next without anyone choosing. Clicking any row — done, next
 * or later — opens that one instead, which is how a declarant reads ahead
 * ("what does step 5 want from me?") without losing their place: the override
 * is session-only and never touches completion.
 *
 * ── Height ─────────────────────────────────────────────────────────────────
 * The cell is elastic — it grew from ~221px to ~500px when Home was rebuilt
 * around this panel (phase 5) — so the layout is written to absorb whatever it
 * gets:
 *
 *   headline + progress    fixed
 *   the rail               FLEXES, and scrolls when the open step is rich
 *
 * Collapsed rows are cheap (~47px with their one-line description), so even a
 * short cell shows several steps of the path around the open one.
 */

// A quick settle, so advancing a step reads as movement along a path rather
// than a re-render.
const EASE = [0.22, 1, 0.36, 1];

// Acronyms keep their case — "needs sefa" reads like a typo.
const sentenceCase = (label) => (label === label.toUpperCase() ? label : label.toLowerCase());

/** The rail's marker: a tick when done, the step number otherwise. */
const StepMarker = ({ index, item, isOpen }) => {
  if (item.completed) {
    return (
      <span className="w-[22px] h-[22px] rounded-full bg-primaryColor/10 text-primaryColor inline-flex items-center justify-center shrink-0">
        <FontAwesomeIcon
          icon={faCheck}
          className="text-[10px]"
        />
      </span>
    );
  }
  if (isOpen) {
    return (
      <span className="w-[22px] h-[22px] rounded-full bg-primaryColor text-white text-[10.5px] font-bold inline-flex items-center justify-center shrink-0 shadow-[0_0_0_3px_rgba(0,184,148,0.15)]">
        {index + 1}
      </span>
    );
  }
  return (
    <span
      className={`w-[22px] h-[22px] rounded-full text-[10px] font-bold text-slate-400 inline-flex items-center justify-center shrink-0 border-[1.5px] ${
        item.isOptional ? 'border-dashed border-slate-200' : 'border-slate-200'
      }`}
    >
      {index + 1}
    </span>
  );
};

const OnboardingGuidedPath = ({
  items,
  onNavigate,
  onOpenItem,
  onPlayVideo,
  onDismiss,
  isDismissing,
  canDismiss,
}) => {
  const { t } = useTranslation('onboarding');
  const reduceMotion = useReducedMotion();
  // `null` = follow the path (open whatever is current). A key = the reader
  // opened that step themselves. `''` = they closed the open one, and no step
  // key can match it. Session-only, as the previous panel behaved.
  const [openKey, setOpenKey] = useState(null);

  // Which regime's files the upload steps talk about (CG-2312 D1: a declarant
  // is entirely UK or entirely EU). Absent `regulation` resolves to EU, so the
  // path renders its EU reading while the fetch is in flight — same field, same
  // default as the route guards and the Upload hub dropzone.
  const { data: declarant } = useFetchDeclarant();
  const ukVariant = isUkRegulation(declarant);

  // Rebuilt when the locale changes, because `t` is a new function each time.
  const path = useMemo(() => buildOnboardingPath(t, { ukVariant }), [t, ukVariant]);
  const sorted = useMemo(() => sortItemsForPath(items), [items]);
  const stats = useMemo(() => getPathStats(sorted), [sorted]);
  const stepUploads = useMemo(() => buildStepUploads(t, { ukVariant }), [t, ukVariant]);

  const currentKey = sorted[stats.currentIndex]?.key ?? null;
  const openStepKey = openKey === null ? currentKey : openKey;

  const toggleStep = (key) => setOpenKey(openStepKey === key ? '' : key);

  const startStep = (item) => {
    const { route } = getItemMeta(item);
    if (route) onNavigate(route);
    else onOpenItem(item);
  };

  // The how-to's `[label](url)` steps render as `data-internal-link` anchors
  // (same convention as the item modal's `renderSimpleMarkdown`) — caught here
  // in the capture phase and routed through `onNavigate` instead of letting
  // the browser full-page-load them.
  const railRef = useRef(null);
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const handleClick = (e) => {
      const link = e.target.closest('a[data-internal-link]');
      if (!link) return;
      e.preventDefault();
      e.stopPropagation();
      onNavigate(link.getAttribute('href'));
    };
    el.addEventListener('click', handleClick, true);
    return () => el.removeEventListener('click', handleClick, true);
  }, [onNavigate]);

  return (
    <div className="h-full flex flex-col min-h-0 overflow-hidden">
      {/* Band 1 — what the whole rail is for and how far along it you are.
          Always present, so the rail below never shifts as steps complete. No
          minutes here: every step already carries its own estimate, and a
          running total on the headline reads as a countdown rather than
          progress. */}
      <div className="shrink-0 mb-2.5">
        {/* The headline and the dismiss control share a row — with the goal
            tabs gone there is nothing else competing for the width, and the
            title is short enough to keep its own line beside a 30px button. */}
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-[15px] font-bold text-slate-900 m-0 leading-tight truncate">
            {path.panelTitle}
          </h2>
          {/* Unlabelled: hiding the checklist is a rare and reversible act that
              shouldn't compete with the headline for width. */}
          {canDismiss && (
            <button
              type="button"
              disabled={isDismissing}
              onClick={onDismiss}
              title={t('guidedPath.hideChecklist', 'Hide checklist')}
              aria-label={t('guidedPath.hideChecklist', 'Hide checklist')}
              className="w-[30px] h-[30px] shrink-0 -mt-1.5 -mr-1 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
            >
              <FontAwesomeIcon
                icon={faEyeSlash}
                className="text-[12px]"
              />
            </button>
          )}
        </div>
        <p className="text-[11.5px] text-slate-500 m-0 mt-1 leading-none">
          {t('guidedPath.progress', '{{completed}} of {{total}} done', {
            completed: stats.completedCount,
            total: stats.total,
          })}
        </p>
        <span className="block w-full h-2.5 rounded-full bg-slate-200/80 overflow-hidden mt-2.5">
          <motion.span
            className="block h-2.5 rounded-full bg-primaryColor"
            initial={false}
            animate={{ width: `${stats.percentage}%` }}
            transition={{ duration: reduceMotion ? 0 : 0.4, ease: EASE }}
          />
        </span>
      </div>

      {/* Band 2 — the rail. Every step, in path order, with one of them open.
          This is the band that absorbs the panel's height: the rows GROW into
          whatever slack the cell has (`min-h-full` + `grow`), so a tall cell
          spreads the path out rather than stacking it at the top over a block
          of dead space. The last row never grows, or the slack would pile up
          under it and put the gap back. Growth is capped so a two-step
          checklist doesn't stretch into a ladder, and it simply doesn't happen
          when there's no slack — then the band scrolls instead. */}
      <div
        ref={railRef}
        className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1"
      >
        <div className="min-h-full flex flex-col">
          {sorted.map((item, index) => {
            const meta = getItemMeta(item, t);
            const isOpen = item.key === openStepKey;
            const isLast = index === sorted.length - 1;
            const uploads = stepUploads[item.key];
            // Three lines is what the card has room for beside the video, and
            // what a reader will actually take in before acting; the modal
            // still holds the full markdown.
            const howTo = isOpen ? parseStepHowTo(item.steps, 4) : [];
            // Presentational only — nothing is actually gated, so this explains
            // the order rather than enforcing it.
            const blocker = item.completed ? null : findBlockingStep(sorted, index);

            return (
              <div
                key={item.key}
                // `items-start`, not `stretch`: a grown row must put its slack
                // UNDER the step, not stretch the open card into a box with a
                // hollow bottom.
                className={`relative flex gap-2.5 items-start shrink-0 ${
                  isLast ? '' : 'grow'
                } ${isOpen ? '' : 'max-h-[80px]'}`}
              >
                {/* The rail: from under this marker to just above the next
                    one. Absolute rather than a flex sibling because a collapsed
                    row can leave ~4px under its marker, and a flexed line there
                    rounds to nothing.

                    Both ends clear the circles they meet, and the current step's
                    circle is 3px bigger than it measures — its ring paints
                    outside the box. Flush against that ring is what made the
                    line look like it ran into the marker. Hence: start at 27px
                    (3 + 22 + 2) or 39px when this row is the open one (11 + 22
                    + 3 of ring + 3), and stop 3px into the next row, or 5px
                    when that next row is the open one — its marker sits lower
                    and brings its ring with it. */}
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className={`absolute left-[16px] w-px bg-slate-200 ${
                      isOpen ? 'top-[36px]' : 'top-[24px]'
                    } ${sorted[index + 1]?.key === openStepKey ? 'bottom-[-8px]' : 'bottom-[-3px]'}`}
                  />
                )}
                {/* Marker centred on the first line of the row's title: the
                    open card's header pads 10px before an 18px line, a
                    collapsed row 6px before a 15px one. */}
                {/* 32px of gutter for a 22px circle, centred — 5px a side, so
                    the current step's 3px ring has 2px of slack.

                    That ring paints OUTSIDE the marker's box, and every
                    ancestor that could clip it does: the rail scrolls
                    (`overflow-y-auto` forces the horizontal axis to clip), the
                    panel root is `overflow-hidden`, and so is the Home section
                    around it. Padding the scroller didn't help — the negative
                    margin that kept the rail in place put that padding right
                    back outside the panel, to be clipped again. Sizing the
                    gutter means nothing paints outside a box in the first
                    place. Measured at exactly 0px of room first, which still
                    shaved the edge: flush is not clear.

                    `flex`, not a block: the marker is `inline-flex`, so in a
                    block wrapper it sits on a text baseline and picks up ~4px
                    of leading above it — which pushed the circle down into the
                    rail line below it. */}
                <div className="shrink-0 w-[32px]">
                  <div className={`flex justify-center ${isOpen ? 'mt-[11px]' : 'mt-[3px]'}`}>
                    <StepMarker
                      index={index}
                      item={item}
                      isOpen={isOpen}
                    />
                  </div>
                </div>

                <div
                  className={`flex-1 min-w-0 mb-1 ${
                    isOpen
                      ? 'border border-primaryColor/25 rounded-xl overflow-hidden bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
                      : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleStep(item.key)}
                    aria-expanded={isOpen}
                    title={item.name}
                    className={`w-full flex items-start gap-2.5 text-left group transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40 ${
                      isOpen
                        ? 'px-3.5 py-2.5 bg-primaryColor/[0.06]'
                        : 'px-1 py-1.5 -ml-1 rounded-lg hover:bg-slate-50'
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block transition-colors ${
                          isOpen
                            ? 'text-[14px] font-bold text-slate-900 leading-snug'
                            : item.completed
                              ? 'text-[12px] text-slate-400 line-through truncate'
                              : 'text-[12px] text-slate-600 group-hover:text-slate-900 truncate'
                        }`}
                      >
                        {item.name}
                      </span>
                      {/* What the step is and why it exists. Collapsed it's one
                          truncated line — a tall panel is better filled with
                          this than with the air the rows would otherwise grow
                          into. Open, it gets its full two lines, because that's
                          the first thing a new declarant reads. */}
                      {item.description && (isOpen || !item.completed) && (
                        <span
                          className={
                            isOpen
                              ? 'block text-[11.5px] text-slate-600 leading-snug mt-1'
                              : 'block truncate text-[11px] text-slate-400 mt-0.5'
                          }
                        >
                          {item.description}
                        </span>
                      )}
                    </span>

                    {isOpen ? (
                      <span className="text-[10px] font-semibold whitespace-nowrap px-1.5 py-0.5 mt-px rounded bg-white border border-slate-200 text-slate-600 shrink-0">
                        {t('guidedPath.minutes', '~{{minutes}} min', { minutes: meta.minutes })}
                      </span>
                    ) : (
                      // No lock hint out here: every blocked step names the same
                      // first outstanding one, so five rows reading "needs
                      // installations" only repeat what their position on the
                      // rail already says. It earns its place on expand, where
                      // someone is about to act.
                      !item.completed && (
                        <span className="text-[11px] text-slate-400 shrink-0 whitespace-nowrap mt-px">
                          {item.isOptional
                            ? t('guidedPath.optionalMinutes', 'optional · ~{{minutes}} min', {
                                minutes: meta.minutes,
                              })
                            : t('guidedPath.minutes', '~{{minutes}} min', {
                                minutes: meta.minutes,
                              })}
                        </span>
                      )
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        animate={reduceMotion ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
                        exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{ duration: reduceMotion ? 0 : 0.2, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div className="px-3.5 pt-2.5 pb-3">
                          {/* How the step actually works, in the seed's own
                              words, next to the walkthrough. This is what a new
                              declarant needs before they'll click anything, and
                              it used to be a modal they had to know to open. */}
                          {(howTo.length > 0 || item.videoUrl) && (
                            <div className="flex gap-3 items-start mb-2.5">
                              {howTo.length > 0 && (
                                <ol className="flex-1 min-w-0 m-0 p-0 list-none space-y-1.5">
                                  {howTo.map((line, stepIndex) => (
                                    <li
                                      key={line}
                                      className="flex gap-2 items-start"
                                    >
                                      <span className="w-[16px] h-[16px] mt-px rounded-full bg-slate-100 text-slate-500 text-[9px] font-bold inline-flex items-center justify-center shrink-0">
                                        {stepIndex + 1}
                                      </span>
                                      {/* `line` is `parseStepHowTo`'s own HTML
                                          output — internal links and bold from
                                          the seed's markdown, not user input. */}
                                      <span
                                        className="text-[11.5px] text-slate-700 leading-snug"
                                        dangerouslySetInnerHTML={{ __html: line }}
                                      />
                                    </li>
                                  ))}
                                </ol>
                              )}
                              <OnboardingStepVideo
                                videoUrl={item.videoUrl}
                                title={item.name}
                                onPlay={() => onPlayVideo(item)}
                              />
                            </div>
                          )}

                          {/* An upload step's options: which file to bring. */}
                          {uploads && (
                            <OnboardingUploadOptions
                              options={uploads.options}
                              hint={uploads.hint}
                            />
                          )}

                          {/* The shortcut the path's one-step-at-a-time shape
                              hides: the same hub takes every data step at once
                              if the workbook is already filled in. */}
                          {uploads && (
                            <p className="flex items-start gap-1.5 text-[11px] text-slate-500 leading-snug m-0 mb-2.5">
                              <FontAwesomeIcon
                                icon={faLayerGroup}
                                className="text-[10px] text-slate-400 mt-0.5 shrink-0"
                              />
                              <span>{uploadHubNote(t)}</span>
                            </p>
                          )}

                          {/* One action row for every step, however many
                              options it listed: they all end in the same Upload
                              hub, so a button per option would be the same
                              button twice. `mt-4` regardless of what it follows
                              — the hub note, the options, or the how-to — so
                              the CTA never crowds the line above it. It has to
                              out-measure that block's own `mb-2.5`, since these
                              are block siblings and the margins collapse to the
                              larger of the two rather than adding up. */}
                          <div className="flex gap-2 items-center flex-wrap mt-4">
                            <button
                              type="button"
                              onClick={() => startStep(item)}
                              className="inline-flex items-center gap-1.5 bg-primaryColor text-white text-[12px] font-medium px-3 py-1.5 rounded-[9px] hover:bg-primaryColor/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
                            >
                              {meta.cta}
                              <FontAwesomeIcon
                                icon={faArrowRight}
                                className="text-[9px]"
                              />
                            </button>
                            {/* Emissions only: the file has to come from a
                                supplier before there is anything to upload. */}
                            {uploads?.send && (
                              <button
                                type="button"
                                onClick={() => onNavigate(uploads.send.route)}
                                className="inline-flex items-center gap-1.5 text-slate-900 border border-slate-300 text-[12px] font-medium px-3 py-1.5 rounded-[9px] hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
                              >
                                <FontAwesomeIcon
                                  icon={faPaperPlane}
                                  className="text-[10px]"
                                />
                                {uploads.send.label}
                              </button>
                            )}
                            {/* No "How it works": the how-to and the
                                walkthrough are both in this card now, so the
                                button pointed at a modal that no longer holds
                                anything the reader can't already see. */}
                            {blocker && (
                              <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 ml-auto min-w-0">
                                <FontAwesomeIcon
                                  icon={faLock}
                                  className="text-[8.5px] shrink-0"
                                />
                                <span className="truncate">
                                  {t('guidedPath.needsStep', 'needs {{step}}', {
                                    step: sentenceCase(getItemMeta(blocker, t).short),
                                  })}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuidedPath;
